import React from 'react'
import { Controller } from 'react-hook-form'

import { useSideEffectSearch } from 'src/shared/hooks/drugLibrary'
import { useSideEffectSearchForm } from 'src/features/tools/hooks/forms'
import { Tools } from 'src/shared/styled'
import Table from 'src/shared/views/Table/Table'
import { COLORS } from 'src/shared/theme'
import { BoldHeading, SubHeaderName } from 'src/shared/styled/Text'
import { ButtonWithMargin, ButtonWithWidth } from 'src/shared/styled/Buttons'
import { SearchContainer } from 'src/shared/styled/Tools'
import TextField from 'src/shared/views/TextField'
import LoadingScreen from 'src/shared/views/LoadingScreen'
import ToolsModal from './ToolsModal'
import { Create } from '@material-ui/icons'
import { Column } from 'react-table'
import { IIndicationAndSideEffect } from 'src/models/program'

const { HeaderContainer, Form, ViewModalLink } = Tools

const formFieldStyles = {
  marginBottom: '30px',
  marginRight: '20px',
  minWidth: '200px',
  width: '50%',
  maxWidth: '250px'
}

const SideEffects = () => {
  const {
    handleSubmit,
    watch,
    control,
    reset,
    formState: { errors, isDirty, isSubmitted }
  } = useSideEffectSearchForm()

  const [modalState, setModalState] = React.useState({
    isOpen: false,
    sideEffect: ''
  })

  const [enableQuery, setEnableQuery] = React.useState(false)
  const [queryValue, setQueryValue] = React.useState('')

  const { data, isLoading, isSuccess } = useSideEffectSearch(
    queryValue,
    10,
    enableQuery
  )

  React.useEffect(() => {
    if (isSuccess) {
      setEnableQuery(false)
    }
  }, [isSuccess])

  const columns = React.useMemo<Column<IIndicationAndSideEffect>[]>(
    () => [
      {
        Header: 'Side Effect',
        accessor: 'name'
      },
      {
        Header: 'Associated Dins',
        Cell: ({ row }: any) => {
          if (!row.original.dins) {
            return []
          }
          return row.original.dins.map((drug: any) => drug.din).join(', ')
        }
      },
      {
        Header: 'Details',
        Cell: ({ row }: any) => (
          <ViewModalLink
            onClick={() =>
              setModalState({
                isOpen: true,
                sideEffect: row.original.id
              })
            }
          >
            <Create />
          </ViewModalLink>
        ),
        disableSortBy: true,
        width: 60
      }
    ],
    []
  )

  const onSubmit = (formValues: any) => {
    setQueryValue(formValues.sideEffect)
    setEnableQuery(true)
  }

  const resetForm = () => {
    setQueryValue('')
    setEnableQuery(false)
    reset()
  }

  watch()

  return (
    <div>
      <HeaderContainer>
        <SubHeaderName>Side Effects</SubHeaderName>
        <ButtonWithMargin
          variant="contained"
          color="primary"
          onClick={() => setModalState({ ...modalState, isOpen: true })}
        >
          ADD NEW SIDE EFFECT
        </ButtonWithMargin>
      </HeaderContainer>
      <SearchContainer>
        <BoldHeading>Side Effect Search</BoldHeading>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            defaultValue={''}
            render={({ field: { ref, ...props } }) => (
              <TextField
                style={formFieldStyles}
                id="sideEffect"
                label="Side Effect"
                placeholder="Enter a Side Effect"
                inputRef={ref}
                error={!!errors?.sideEffect?.message}
                helperText={errors?.sideEffect?.message}
                {...props}
              />
            )}
            name="sideEffect"
            control={control}
          />
          <div>
            <ButtonWithWidth
              disabled={!isDirty}
              type="submit"
              color="secondary"
            >
              SEARCH
            </ButtonWithWidth>
            <ButtonWithWidth
              type="button"
              variant="outlined"
              onClick={resetForm}
            >
              RESET
            </ButtonWithWidth>
          </div>
        </Form>
      </SearchContainer>
      {isLoading ? (
        <LoadingScreen />
      ) : data && isSubmitted ? (
        <Table
          columns={columns}
          data={data}
          placeholder="Search Side Effects"
          getHeaderProps={() => ({
            style: {
              background: COLORS.white
            }
          })}
        />
      ) : null}
      <ToolsModal
        isOpen={modalState.isOpen}
        handleClose={() => {
          setModalState({ sideEffect: '', isOpen: false })
        }}
        id={modalState.sideEffect}
        type="side-effect"
      />
    </div>
  )
}

export default SideEffects
